import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Button, Form, Spinner } from 'react-bootstrap';
import { PencilFill, CheckLg, XLg, PlusCircle, Trash } from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/SecurityContext';
import RoleGuard from '../security/RoleGuard';
import SectionControls from '../SectionControls';
import { updateCardStepsSection } from '../../services/contentService';
import './CardStepsBlock.scss';

const COLUMN_OPTIONS = [
  { value: 3, label: '3 columns' },
  { value: 4, label: '4 columns' },
];

function defaultStep() {
  return {
    title: 'Step title',
    description: 'One short sentence describing this step.',
  };
}

export function defaultCardStepsData() {
  return {
    title: 'How to participate?',
    intro: '',
    columns: 3,
    steps: [defaultStep(), defaultStep(), defaultStep()],
  };
}

export function CardStepsView({ data }) {
  const steps = Array.isArray(data?.steps) ? data.steps : [];
  const columns = data?.columns === 4 ? 4 : 3;
  const colSpan = columns === 4 ? 3 : 4;
  return (
    <div className="card-steps-block">
      {data?.title ? <h2 className="card-steps-block__title">{data.title}</h2> : null}
      {data?.intro ? <p className="card-steps-block__intro">{data.intro}</p> : null}
      <Row className="card-steps-block__steps g-3">
        {steps.map((step, idx) => (
          <Col key={idx} md={6} lg={colSpan} className="card-steps-block__step-col">
            <div className="card-steps-block__step">
              <div className="card-steps-block__number">{idx + 1}</div>
              <div className="card-steps-block__step-title">{step.title}</div>
              <div className="card-steps-block__step-description">{step.description}</div>
            </div>
          </Col>
        ))}
      </Row>
    </div>
  );
}

function CardStepsBlock({
  pageId, section, onMoveUp, onMoveDown, onDelete, isFirstSection, isLastSection,
}) {
  const { user } = useAuth();
  const initialData = useMemo(() => section?.data || defaultCardStepsData(), [section]);
  const [data, setData] = useState(initialData);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setData(section?.data || defaultCardStepsData());
  }, [section]);

  async function handleSave() {
    setIsSaving(true);
    try {
      const modifiedBy = user?.attributes?.email || user?.username || 'user';
      await updateCardStepsSection(pageId, section.position, data, modifiedBy);
      setIsEditing(false);
    } catch (e) {
      console.error('Error saving card steps:', e);
      // eslint-disable-next-line no-alert
      alert('Failed to save section. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel() {
    setData(section?.data || defaultCardStepsData());
    setIsEditing(false);
  }

  function updateField(field, value) {
    setData((prev) => ({ ...prev, [field]: value }));
  }

  function updateStep(idx, patch) {
    setData((prev) => {
      const next = { ...prev, steps: [...(prev.steps || [])] };
      next.steps[idx] = { ...next.steps[idx], ...patch };
      return next;
    });
  }

  function addStep() {
    setData((prev) => ({ ...prev, steps: [...(prev.steps || []), defaultStep()] }));
  }

  function removeStep(idx) {
    setData((prev) => {
      const next = { ...prev, steps: [...(prev.steps || [])] };
      next.steps.splice(idx, 1);
      return next;
    });
  }

  if (isEditing) {
    return (
      <div className="card-steps-block card-steps-block--editing">
        <Row className="g-2 mb-3">
          <Col md={8}>
            <Form.Label>Section title</Form.Label>
            <Form.Control
              type="text"
              value={data.title || ''}
              onChange={(e) => updateField('title', e.target.value)}
            />
          </Col>
          <Col md={4}>
            <Form.Label>Columns</Form.Label>
            <Form.Select
              value={data.columns || 3}
              onChange={(e) => updateField('columns', parseInt(e.target.value, 10))}
            >
              {COLUMN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Form.Select>
          </Col>
          <Col md={12}>
            <Form.Label>Intro paragraph (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={data.intro || ''}
              onChange={(e) => updateField('intro', e.target.value)}
            />
          </Col>
        </Row>

        {(data.steps || []).map((step, idx) => (
          <div key={idx} className="card-steps-block__editor-step">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Step {idx + 1}</strong>
              <Button variant="outline-danger" size="sm" onClick={() => removeStep(idx)}>
                <Trash /> Remove
              </Button>
            </div>
            <Row className="g-2">
              <Col md={4}>
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  value={step.title || ''}
                  onChange={(e) => updateStep(idx, { title: e.target.value })}
                />
              </Col>
              <Col md={8}>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={step.description || ''}
                  onChange={(e) => updateStep(idx, { description: e.target.value })}
                />
              </Col>
            </Row>
          </div>
        ))}

        <Button variant="outline-primary" size="sm" onClick={addStep} className="mb-3">
          <PlusCircle /> Add step
        </Button>

        <div className="card-steps-block__editor-actions">
          <Button variant="success" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <><Spinner as="span" animation="border" size="sm" className="me-2" />Saving…</>
            ) : (
              <><CheckLg /> Save</>
            )}
          </Button>
          <Button variant="secondary" onClick={handleCancel} disabled={isSaving}>
            <XLg /> Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="card-steps-block card-steps-block--view">
      <CardStepsView data={data} />
      <RoleGuard role="admin">
        <div className="admin-controls">
          <Button
            variant="outline-secondary"
            size="sm"
            className="edit-btn"
            onClick={() => setIsEditing(true)}
          >
            <PencilFill /> Edit steps
          </Button>
          <SectionControls
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            onDelete={onDelete}
            isFirstSection={isFirstSection}
            isLastSection={isLastSection}
          />
        </div>
      </RoleGuard>
    </div>
  );
}

export default CardStepsBlock;
